import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import AgenticReportingContext from "./AgenticReportingContext";
import toastContext from "../toast/ToastContext";
import { useTranslation } from "react-i18next";
import {
  getLLMAgentList,
  getStylesList,
} from "../../service/MasterService";

import {
  isLLMAgentPresent,
  loadDicomAndDrawRectangle,
  removeMaskImageAnnotation,
} from "../../service/MaskImageService";
import {
  generateRandomString,
  slateJsonToPlainText,
} from "../../helpers/CommonUtility";
import { TOAST_TYPE } from "../../constants/Constants";
import studyReportContext from "../studyreport/StudyReportContext";
import ViewportContext from "../studyviewer/ViewportContext";
import studyviewerContext from "../studyviewer/StudyviewerContext";
import loaderContext from "../loader/LoaderContext";
import {
  regenerateLLMReport,
  logAgenticEvent,
} from "../../service/StudyReportService";
import userContext from "../users/UserContext";
import { Editor } from "slate";

const AgenticReportingState = (props) => {
  const {
    modality,
    allReportTemplates,
    allReportTemplatesLoading,
    reportDoneStatusState,
    reportSavedBy,
    setPatientReports,
    setCurrentActiveTab,
    setModel,
    setActiveTab,
    setSelectedCategoryTemplate,
    patientReports,
    agenticInterfaceRef,
    handleModelChange,
    isExternal,
    studyId,
    observationState,
    alertSuccessModal,
    setAlertSuccessModal,
    reportTitle,
    llmReportTitle,
    defaultAgenticTemplate,
    editorRef,
    setHasUnsavedChanges,
    markReportAutosaveDirty,
    setReportTitle,
    handleChangeTemplate,
    activeTabKey,
    setActiveTabKey,
    setDisableObservations,
    firmId,
    studyParentId,
    maskImages,
    setMaskImages,
    studyClinicalNotesData,
  } = useContext(studyReportContext);

  const { ref, setLoaderRunning } = useContext(loaderContext);
  const viewerContextData = useContext(ViewportContext);
  const disableMasking = viewerContextData?.disableMasking ?? (() => {});
  const studyViewerData = useContext(studyviewerContext);
  const autoOpenReportPanel = studyViewerData?.autoOpenReportPanel ?? false;

  const { userId } = useContext(userContext);
  const { showToastv2 } = useContext(toastContext);
  const [t] = useTranslation();

  const [agenticReportAgents, setAgenticReportAgents] = useState([]);
  const [styles, setStyles] = useState([]);
  const [clientFirmList, setClientFirmList] = useState([]);

  // Vapi
  const [isVapiConnecting, setIsVapiConnecting] = useState(false);
  const [isVapiStenographActive, setIsVapiStenographActive] = useState(false);
  const [vapiFunctions, setVapiFunctions] = useState({
    startAssistant: () => console.log("Vapi context not ready"),
    stopAssistant: () => console.log("Vapi context not ready"),
    confirmVapiTranscript: () => console.log("Vapi context not ready"),
    cancelVapiTranscript: () => console.log("Vapi context not ready"),
  });

  // UI States
  const [isAgenticTab, setIsAgenticTab] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const chatInputRef = useRef(null);
  const chatInputRefs = useRef({});

  const defaultTabState = {
    inputValue: "",
    userPrompts: [],
    llmSessionId: "",
    firstAgenticLLMReport: "",
    sessionUserTemplate: null,
    resetLLMReportClicked: false,
    isFirstCall: true,
    voiceAssistantSummary: "",
    reportFromAPI: "",
    disableResetReport: true,
    disableCopyButton: true,
    selectedReportMention: null,
    currentVapiTranscript: "",
    isMicRecording: false,
    isInstructionCollapsed: true,
    hasAutoPopulated: false,
    stenographState: {
      userSpeaking: false,
      assistantSpeaking: false,
      amplitude: 0,
      isMuted: false,
    },
    micStartTime: null,
    deepgramUsage: 0,
  };

  const [tabChatState, setTabChatState] = useState({});

  const initializeAgenticTab = useCallback(
    (newTabKey, initialReportContent = "", selectedAgent = null) => {
      setActiveTabKey(newTabKey);
      setTabChatState((prevState) => ({
        ...prevState,
        [newTabKey]: {
          ...defaultTabState,
          llmSessionId: generateRandomString(userId, studyId),
          sessionUserTemplate: initialReportContent || null,
          reportFromAPI: initialReportContent,
          selectedReportMention: selectedAgent,
        },
      }));
    },
    [studyId, userId, setActiveTabKey],
  );

  const resetAgenticState = useCallback(() => {
    setTabChatState({});
    setIsAgenticTab(false);
    setIsTalking(false);
    setIsVapiStenographActive(false);
    setIsVapiConnecting(false);
  }, []);

  const hasTextChanged = (html1, html2) => {
    const extractCleanText = (html) => {
      const div = document.createElement("div");
      const spacedHtml = (html || "").replace(/<[^>]+>/g, " ");

      div.innerHTML = spacedHtml;
      let text = div.textContent || "";

      return text
        .replace(/\u00A0/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    };
    return extractCleanText(html1) !== extractCleanText(html2);
  };

  const logSubmitEvents = useCallback(() => {
    try {
      Object.entries(tabChatState).forEach(([tabKey, tabState]) => {
        if (!tabState?.llmSessionId || tabState.llmSessionId === "") {
          return;
        }

        const matchingReport = patientReports.find(
          (report) => String(report.key) === String(tabKey),
        );

        if (!matchingReport) {
          return;
        }

        const reportTitleForTab =
          matchingReport.reportDetails?.reportTemplateTitle ||
          matchingReport.reportDetails?.title ||
          matchingReport.text;

        if (!reportTitleForTab?.toLowerCase().startsWith("agentic")) {
          return;
        }

        const matchingAgent = agenticReportAgents.find(
          (agent) =>
            agent.label?.toLowerCase() === reportTitleForTab?.toLowerCase(),
        );

        const agentId = matchingAgent?.value || null;

        let currentReportText = matchingReport.text || "";
        if (
          String(activeTabKey) === String(tabKey) &&
          editorRef?.current?.editor?.html
        ) {
          currentReportText =
            editorRef.current.editor.html.get() || currentReportText;
        }

        const isEdited = hasTextChanged(currentReportText, tabState.reportFromAPI)
          ? 1
          : 0;

        let calculatedDeepgramUsage = tabState.deepgramUsage || 0;
        if (tabState.isMicRecording && tabState.micStartTime) {
          calculatedDeepgramUsage += Math.round(
            (Date.now() - tabState.micStartTime) / 1000,
          );
        }

        logAgenticEvent({
          studyId,
          firmId: parseInt(firmId, 10),
          sessionId: tabState.llmSessionId,
          agentId,
          eventName: "REPORT_SUBMIT",
          observation: null,
          reportEdited: isEdited,
          deepgramUsage: calculatedDeepgramUsage,
          source: "augmento",
        });
      });
    } catch (error) {
      console.error("[AgenticState] Failed to log submit events:", error);
    }
  }, [
    tabChatState,
    patientReports,
    activeTabKey,
    editorRef,
    agenticReportAgents,
    firmId,
    studyId,
  ]);

  useEffect(() => {
    const checkIsAgentic = reportTitle
      ? reportTitle.toLowerCase().startsWith("agentic")
      : false;
    setIsAgenticTab(checkIsAgentic);
  }, [reportTitle, activeTabKey, patientReports]);

  useEffect(() => {
    if (agenticInterfaceRef) {
      agenticInterfaceRef.current = {
        initializeTab: initializeAgenticTab,
        resetAgenticState: resetAgenticState,
        logSubmitEvents: logSubmitEvents,
      };
    }

    return () => {
      if (agenticInterfaceRef) {
        agenticInterfaceRef.current = null;
      }
    };
  }, [agenticInterfaceRef, initializeAgenticTab, resetAgenticState, logSubmitEvents]);

  useEffect(() => {
    if (
      llmReportTitle != undefined &&
      llmReportTitle != "" &&
      !allReportTemplatesLoading &&
      Object.keys(allReportTemplates).length != 0 &&
      reportDoneStatusState === 0 &&
      reportSavedBy === 0
    ) {
      let opt = [];
      let newTabKey = null;
      allReportTemplates?.dsrReportTemplates?.forEach((rt) => {
        rt.report = defaultAgenticTemplate;
        rt.reportTemplateTitle = llmReportTitle;
        if (rt.modality.name === modality) {
          newTabKey = rt.id;
          opt.push({ key: newTabKey, text: rt.title, reportDetails: rt });
          setCurrentActiveTab(rt.title);
          setModel(rt.finalReport);
        }
      });
      if (llmReportTitle.includes("Other")) {
        setDisableObservations(true);
        setActiveTab(1);
      } else {
        setDisableObservations(false);
        if (autoOpenReportPanel) {
          setActiveTab(1);
        }
      }
      if (opt.length > 0) {
        setPatientReports(opt);
        setActiveTabKey(newTabKey);
        const matchingAgent = agenticReportAgents?.find(
          (a) => a.label?.toLowerCase() === llmReportTitle?.toLowerCase(),
        );
        const initialMention = llmReportTitle
          ? {
            label: llmReportTitle,
            value: matchingAgent?.value ?? null,
          }
          : null;

        setTabChatState((prevState) => ({
          ...prevState,
          [newTabKey]: {
            ...defaultTabState,
            llmSessionId: generateRandomString(userId, studyId),
            reportFromAPI: defaultAgenticTemplate,
            selectedReportMention: initialMention,
          },
        }));
        setSelectedCategoryTemplate("dsrTemplate");
      }
    }
  }, [
    llmReportTitle,
    allReportTemplatesLoading,
    allReportTemplates,
    modality,
    defaultAgenticTemplate,
  ]);

  useEffect(() => {
    if (patientReports && patientReports.length > 0) {
      setTabChatState((prevState) => {
        let hasChanges = false;
        const newState = { ...prevState };

        patientReports.forEach((report) => {
          const title = report.reportDetails?.title || report.text || "";
          if (
            title.toLowerCase().startsWith("agentic") &&
            !newState[report.key]
          ) {
            hasChanges = true;
            newState[report.key] = {
              ...defaultTabState,
              llmSessionId: generateRandomString(userId, studyId),
              reportFromAPI: report.reportDetails?.finalReport || report.text || "",
            };
          }
        });

        if (hasChanges) {
          return newState;
        }
        return prevState;
      });
    }
  }, [patientReports, agenticReportAgents, userId, studyId]);

  const prevActiveTabKeyRef = useRef(activeTabKey);
  useEffect(() => {
    if (prevActiveTabKeyRef.current !== activeTabKey) {
      const prevKey = prevActiveTabKeyRef.current;

      setTabChatState((prev) => {
        if (prev[prevKey]?.isMicRecording) {
          console.log(
            `[AgenticState] Auto-stopping Mic for inactive tab: ${prevKey}`,
          );
          return {
            ...prev,
            [prevKey]: {
              ...prev[prevKey],
              isMicRecording: false,
            },
          };
        }
        return prev;
      });

      prevActiveTabKeyRef.current = activeTabKey;
    }
  }, [activeTabKey]);

  const activeState = tabChatState[activeTabKey] || defaultTabState;

  const {
    inputValue,
    userPrompts,
    llmSessionId,
    firstAgenticLLMReport,
    sessionUserTemplate,
    isFirstCall,
    voiceAssistantSummary,
    reportFromAPI,
    currentVapiTranscript,
    stenographState,
    disableResetReport,
    disableCopyButton,
    resetLLMReportClicked,
    isMicRecording,
    isInstructionCollapsed,
    selectedReportMention,
    hasAutoPopulated,
  } = activeState;

  const updateActiveTabState = (updates) => {
    setTabChatState((prev) => ({
      ...prev,
      [activeTabKey]: {
        ...(prev[activeTabKey] || defaultTabState),
        ...updates,
      },
    }));
  };

  const setInputValue = (val) => updateActiveTabState({ inputValue: val });

  const setUserPrompts = (valOrFunc) => {
    setTabChatState((prev) => {
      const currentState = prev[activeTabKey] || defaultTabState;
      const newValue =
        typeof valOrFunc === "function"
          ? valOrFunc(currentState.userPrompts)
          : valOrFunc;
      return {
        ...prev,
        [activeTabKey]: {
          ...currentState,
          userPrompts: newValue,
          isInstructionCollapsed: false,
        },
      };
    });
  };


  const setIsInstructionCollapsed = (val) =>
    updateActiveTabState({ isInstructionCollapsed: val });
  const setHasAutoPopulated = (val) =>
    updateActiveTabState({ hasAutoPopulated: val });
  const setLlmSessionId = (val) => updateActiveTabState({ llmSessionId: val });
  const setFirstAgenticLLMReport = (val) =>
    updateActiveTabState({ firstAgenticLLMReport: val });
  const setSessionUserTemplate = (val) =>
    updateActiveTabState({ sessionUserTemplate: val });
  const setIsFirstCall = (val) => updateActiveTabState({ isFirstCall: val });
  const setVoiceAssistantSummary = (val) =>
    updateActiveTabState({ voiceAssistantSummary: val });
  const setReportFromAPI = (val) =>
    updateActiveTabState({ reportFromAPI: val });
  const setCurrentVapiTranscript = (val) =>
    updateActiveTabState({ currentVapiTranscript: val });

  const setStenographState = (valOrFunc) => {
    setTabChatState((prev) => {
      const currentState = prev[activeTabKey] || defaultTabState;
      const newValue =
        typeof valOrFunc === "function"
          ? valOrFunc(currentState.stenographState)
          : valOrFunc;
      return {
        ...prev,
        [activeTabKey]: { ...currentState, stenographState: newValue },
      };
    });
  };

  const setDisableResetReport = (val) =>
    updateActiveTabState({ disableResetReport: val });
  const setDisableCopyButton = (val) =>
    updateActiveTabState({ disableCopyButton: val });
  const setResetLLMReportClicked = (val) =>
    updateActiveTabState({ resetLLMReportClicked: val });

  const setIsMicRecording = (valOrFunc) => {
    setTabChatState((prev) => {
      const currentState = prev[activeTabKey] || defaultTabState;
      const newValue =
        typeof valOrFunc === "function"
          ? valOrFunc(currentState.isMicRecording)
          : valOrFunc;

      let micStartTime = currentState.micStartTime;
      let deepgramUsage = currentState.deepgramUsage || 0;

      if (newValue && !currentState.isMicRecording) {
        micStartTime = Date.now();
      } else if (!newValue && currentState.isMicRecording) {
        if (micStartTime) {
          const duration = Math.round((Date.now() - micStartTime) / 1000);
          deepgramUsage += duration;
          micStartTime = null;
        }
      }

      return {
        ...prev,
        [activeTabKey]: {
          ...currentState,
          isMicRecording: newValue,
          micStartTime,
          deepgramUsage
        },
      };
    });
  };

  const setSelectedReportMention = (val) =>
    updateActiveTabState({ selectedReportMention: val });

  const fetchLLMAgentList = async () => {
    try {
      const response = await getLLMAgentList();
      const agentList = [];

      if (response?.data?.data && Array.isArray(response.data.data)) {
        response.data.data.forEach((agent) => {
          agentList.push({
            value: agent.id,
            label: agent.title,
          });
        });
        agentList.sort((a, b) => a.label.localeCompare(b.label));
        setAgenticReportAgents(agentList);
      } else {
        setAgenticReportAgents([]);
      }
    } catch (error) {
      console.error("Failed to fetch or process agent list:", error);
      setAgenticReportAgents([]);
    }
  };

  const fetchAgenticStylesAndTemplates = async (refreshFlag = 0) => {
    try {
      const response = await getStylesList(refreshFlag);
      if (response?.data?.result === "SUCCESS") {
        const { clientFirmList, styles } = response.data.data;
        setClientFirmList(clientFirmList);
        setStyles(styles);
      } else {
        console.error("Failed to fetch styles list:", response);
      }
    } catch (err) {
      console.error("Error while fetching styles list:", err);
    }
  };

  useEffect(() => {
    if (firmId != null && firmId != undefined && studyParentId != null) {
      fetchLLMAgentList();
      fetchAgenticStylesAndTemplates();
    }
  }, [studyParentId, firmId]);

  // const registerChatInputRef = (tabId, editor) => {
  //   chatInputRefs.current[tabId] = editor;
  // };

  // const getChatInputRef = (tabId) => chatInputRefs.current[tabId] || null;

  // 1. UPDATED REGISTER FUNCTION (With Logs & useCallback)
  const registerChatInputRef = useCallback((tabId, editor) => {
    if (!tabId) {
      console.warn("registerChatInputRef called with missing tabId");
      return;
    }
    // console.log(`[AgenticState] Registering Editor for Tab: ${tabId}`);
    chatInputRefs.current[tabId] = editor;

    // Debug: Print all current keys to verify storage
    // console.log("[AgenticState] Current Chat Refs:", Object.keys(chatInputRefs.current));
  }, []);

  // 2. UPDATED GET FUNCTION (With Logs & useCallback)
  const getChatInputRef = useCallback((tabId) => {
    // console.log(`[AgenticState] Retrieval requested for Tab: ${tabId}`);
    const editor = chatInputRefs.current[tabId];

    if (!editor) {
      /*console.warn(
        `[AgenticState] Editor NOT found for Tab: ${tabId}. Available keys:`,
        Object.keys(chatInputRefs.current),
      );*/
    }
    return editor || null;
  }, []);

  const getCurrentMentionFromInput = () => {
    const currentChatEditor = getChatInputRef
      ? getChatInputRef(activeTabKey)
      : null;
    if (currentChatEditor) {
      try {
        const [mentionEntry] = Editor.nodes(currentChatEditor, {
          at: [],
          match: (n) => n.type === "mention",
        });
        if (mentionEntry) {
          const [node] = mentionEntry;
          return { label: node.character, value: node.value };
        }
      } catch (e) {
        console.error("Error reading chat input nodes:", e);
      }
    }
    return null;
  };



  const buildLLMReportDto = async (mentionData = null, isVapiCall = false) => {
    const changed = resetLLMReportClicked
      ? true
      : hasTextChanged(reportFromAPI, editorRef.current.editor.html.get());

    const observationText = isVapiCall
      ? ""
      : slateJsonToPlainText(inputValue).trim();

    let images = [];
    if (!isExternal) {
      const imagePromises = Object.values(maskImages)
        .filter((item) => item)
        .map((item) =>
          loadDicomAndDrawRectangle(item.imageURL, item.maskAnnotationData),
        );
      images = await Promise.all(imagePromises);
    }

    let finalTextReport = "";
    let finalUserTemplate = sessionUserTemplate;

    if (isFirstCall) {
      if (reportDoneStatusState !== 0) {
        finalTextReport = editorRef.current.editor.html.get();
        const selectedAgentTemplateObj = allReportTemplates?.agenticReportTemplates?.find(
          (item) => item.agenticReportTemplateDto?.title === mentionData?.label
        );
        finalUserTemplate = selectedAgentTemplateObj?.agenticReportTemplateDto?.template || "";
        setSessionUserTemplate(finalUserTemplate);
      } else {
        finalTextReport = "";
        finalUserTemplate = editorRef.current.editor.html.get();
        setSessionUserTemplate(finalUserTemplate);
      }
    } else {
      finalTextReport = changed ? editorRef.current.editor.html.get() : "";
    }

    const reportTitle = mentionData?.label || "";

    return {
      sessionId: activeState.llmSessionId,
      studyId,
      radiologistId: userId,
      firmId: parseInt(firmId, 10),
      images: images ?? [],
      observations: observationText,
      clinicalHistory: observationState.clinicalHistory,
      clinicalNotes: studyClinicalNotesData ?? "",
      reportTitle,
      textReport: finalTextReport,
      userTemplate: finalUserTemplate,
      region: "US",
      language: "English",
    };
  };

  const handleLlmTabRemove = (e, tabKeyToRemove, index) => {
    e.stopPropagation();

    const remainingTabs = patientReports.filter(
      (p) => p.key !== tabKeyToRemove,
    );

    if (activeTabKey === tabKeyToRemove) {
      if (remainingTabs.length > 0) {
        const newActiveIndex = Math.max(0, index - 1);
        handleChangeTemplate(remainingTabs[newActiveIndex]);
      } else {
        setActiveTabKey(null);
        setModel("");
        setReportTitle("");
      }
    }

    setPatientReports(remainingTabs);

    setTabChatState((prevState) => {
      const newState = { ...prevState };
      delete newState[tabKeyToRemove];
      return newState;
    });

    if (chatInputRefs.current[tabKeyToRemove]) {
      delete chatInputRefs.current[tabKeyToRemove];
    }

    markReportAutosaveDirty();
    setHasUnsavedChanges(false);
  };

  const cleanUpAgenticTab = (tabKeyToRemove) => {
    setTabChatState((prevState) => {
      const newState = { ...prevState };
      delete newState[tabKeyToRemove];
      return newState;
    });

    if (chatInputRefs.current[tabKeyToRemove]) {
      delete chatInputRefs.current[tabKeyToRemove];
    }
  };

  const handleSuccessfulReport = (finalReport, llmReportDto) => {
    setIsFirstCall(false);
    setReportFromAPI(finalReport);
    handleModelChange(finalReport, false);
    markReportAutosaveDirty();
    setActiveTab(1);
    setDisableCopyButton(false);
    setDisableResetReport(false);

    if (firstAgenticLLMReport === "") {
      setFirstAgenticLLMReport(finalReport);
    }

    if (llmReportDto) {
      const newObject = { prompt: llmReportDto.observations, isNote: false };
      setUserPrompts((prevPrompts) => [newObject, ...prevPrompts]);
    }
    setInputValue("");
    setResetLLMReportClicked(false);

    if (!isExternal) {
      removeMaskImageAnnotation(maskImages);
      setMaskImages([]);
      disableMasking();
    }
  };

  const getVapiConfigPayload = () => {
    const inputMention = getCurrentMentionFromInput();
    const reportTitleValue =
      inputMention?.label || activeState.selectedReportMention?.label || "";

    const payload = {
      sessionId: llmSessionId,
      reportTitle: reportTitleValue,
    };

    if (isFirstCall) {
      payload.previous_summary = "";
    } else {
      const currentReportContent = editorRef.current
        ? editorRef.current.editor.html.get()
        : "";
      payload.current_report = currentReportContent;

      const allInstructions = userPrompts
        .map((item) => slateJsonToPlainText(item.prompt).trim())
        .filter((text) => text.length > 0)
        .join(". ");

      const hasSummary =
        voiceAssistantSummary && voiceAssistantSummary.trim().length > 0;
      const hasInstructions = allInstructions.length > 0;

      if (hasSummary && hasInstructions) {
        payload.previous_summary = `${voiceAssistantSummary}. ${allInstructions}`;
      } else if (hasSummary) {
        payload.previous_summary = voiceAssistantSummary;
      } else if (hasInstructions) {
        payload.previous_summary = allInstructions;
      } else {
        payload.previous_summary = "";
      }
    }
    return payload;
  };

  const handleVoiceReportGeneration = async (vapiArgs) => {
    const {
      reportTitle: voiceTitle,
      observations,
      voice_assistant_summary,
    } = vapiArgs;

    if (voice_assistant_summary) {
      setVoiceAssistantSummary(voice_assistant_summary);
    }

    const inputMention = getCurrentMentionFromInput();

    const currentActiveMention = inputMention || selectedReportMention;
    const currentSessionTitle = currentActiveMention?.label || "";

    let finalDto = null;
    let shouldCallApi = false;

    if (!isFirstCall) {
      finalDto = await buildLLMReportDto(currentActiveMention, true);
      finalDto.observations = "";
      shouldCallApi = true;
    } else {
      if (voiceTitle === currentSessionTitle) {
        setSelectedReportMention(currentActiveMention);
        finalDto = await buildLLMReportDto(currentActiveMention, true);
        finalDto.observations = "";
        shouldCallApi = true;
      } else {
        const agentExists = agenticReportAgents.find(
          (a) => a.label?.toLowerCase() === voiceTitle?.toLowerCase(),
        );

        if (agentExists) {
          const newMention = { label: voiceTitle, value: agentExists.value };

          setSelectedReportMention(newMention);

          finalDto = await buildLLMReportDto(newMention, true);

          finalDto.userTemplate = t("llm-reporting.fetch-on-server");
          finalDto.observations = "";

          shouldCallApi = true;
        } else {
          console.log(
            "First Call: Agent not found, switching to Other Agent workflow:",
            voiceTitle,
          );
          const newMention = { label: voiceTitle, value: null };

          setSelectedReportMention(newMention);

          finalDto = await buildLLMReportDto(newMention, true);
          finalDto.observations = "";

          shouldCallApi = true;
        }
      }
    }

    if (shouldCallApi && finalDto) {
      try {
        const response = await regenerateLLMReport(finalDto);
        if (response.status === 200) {
          return true;
        } else {
          setAlertSuccessModal({
            show: true,
            heading: "Error",
            description: t("llm-reporting.timeout-modal-description"),
          });
          return false;
        }
      } catch (error) {
        console.error("API Error", error);
        return false;
      }
    }
    return false;
  };

  const handleDSRRegenerate = async (mentionData) => {
    setLoaderRunning(true);
    ref.current.continuousStart();
    const llmReportDto = await buildLLMReportDto(mentionData);
    const response = await regenerateLLMReport(llmReportDto);
    setLoaderRunning(false);
    ref.current.complete();
    if (response.status === 200) {
      handleSuccessfulReport(response.data.data.finalReport, llmReportDto);
    } else {
      setAlertSuccessModal(() => {
        const newItem = { ...alertSuccessModal };
        newItem.show = true;
        newItem.heading = "Error";
        newItem.description = response.data.message;
        return newItem;
      });
    }
  };

  const handleResetReportForActiveTab = () => {
    const activeTabState = tabChatState[activeTabKey] || defaultTabState;
    const newSessionId = generateRandomString(userId, studyId);
    handleModelChange(activeTabState.firstAgenticLLMReport, false);

    setTabChatState((prevState) => ({
      ...prevState,
      [activeTabKey]: {
        ...activeTabState,
        llmSessionId: newSessionId,
        reportFromAPI: activeTabState.firstAgenticLLMReport,
        resetLLMReportClicked: true,
        userPrompts: [
          { prompt: t("string-literals.note-old-instructions"), isNote: true },
          ...activeTabState.userPrompts,
        ],
        disableResetReport: true,
        isInstructionCollapsed: false,
      },
    }));

    showToastv2(
      TOAST_TYPE.SUCCESS,
      "Report has been reset. The next prompt will regenerate it.",
    );
  };

  const getAvailableAgents = useCallback(
    () => agenticReportAgents,
    [agenticReportAgents],
  );

  return (
    <AgenticReportingContext.Provider
      value={{
        agenticReportAgents,
        setAgenticReportAgents,
        styles,
        setStyles,
        clientFirmList,
        setClientFirmList,
        isVapiConnecting,
        setIsVapiConnecting,
        isVapiStenographActive,
        setIsVapiStenographActive,
        vapiFunctions,
        setVapiFunctions,
        isTalking,
        setIsTalking,
        voiceAssistantSummary,
        setVoiceAssistantSummary,
        currentVapiTranscript,
        setCurrentVapiTranscript,
        stenographState,
        setStenographState,
        isAgenticTab,
        setIsAgenticTab,
        tabChatState,
        setTabChatState,
        defaultTabState,
        inputValue,
        setInputValue,
        userPrompts,
        setUserPrompts,
        llmSessionId,
        setLlmSessionId,
        firstAgenticLLMReport,
        setFirstAgenticLLMReport,
        sessionUserTemplate,
        setSessionUserTemplate,
        isFirstCall,
        setIsFirstCall,
        reportFromAPI,
        setReportFromAPI,
        disableResetReport,
        setDisableResetReport,
        disableCopyButton,
        setDisableCopyButton,
        resetLLMReportClicked,
        setResetLLMReportClicked,
        selectedReportMention: activeState.selectedReportMention,
        setSelectedReportMention,
        chatInputRef,
        chatInputRefs,
        registerChatInputRef,
        getChatInputRef,
        updateActiveTabState,
        fetchLLMAgentList,
        fetchAgenticStylesAndTemplates,
        hasTextChanged,
        buildLLMReportDto,
        initializeAgenticTab,
        cleanUpAgenticTab,
        handleSuccessfulReport,
        getVapiConfigPayload,
        handleDSRRegenerate,
        handleVoiceReportGeneration,
        handleLlmTabRemove,
        isMicRecording,
        setIsMicRecording,
        isInstructionCollapsed,
        setIsInstructionCollapsed,
        handleResetReportForActiveTab,
        resetAgenticState,
        getAvailableAgents,
        hasAutoPopulated,
        setHasAutoPopulated,
      }}
    >
      {props.children}
    </AgenticReportingContext.Provider>
  );
};

export default AgenticReportingState;
